"use client";

import * as React from "react";
import {
  Background,
  BackgroundVariant,
  Handle,
  MarkerType,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useViewport,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";

import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Group } from "../../components/ui/group";
import { Icon } from "../../components/ui/icon";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { ControlBar, ControlBarButton } from "../control-bar/control-bar";
import { useIsMobile } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";
import { isTextEditingTarget } from "../../lib/text-editing-target";
import type { Omittable } from "../../lib/props";

/**
 * org-chart — a themed, closed façade over React Flow for reporting-line
 * trees (the chart-kit pattern: the flow library never leaks into consumer
 * code). Consumers hand it flat parent-linked items and a content renderer;
 * layout, edges, pan/zoom, collapse/expand, and the node card chrome are
 * owned here.
 *
 * Layout is the ClickUp-classic **indented tree**: a node's reports stack
 * vertically below it, shifted right one indent step; a trunk drops from
 * below the node's left portion and elbows into each report's left edge.
 * Sibling roots line up in a row, each subtree growing downward.
 *
 * The canvas fills its parent — mount it inside a container that has a
 * concrete height.
 */

export interface OrgChartItem<T = unknown> {
  id: string;
  /** The node this one reports to; null (or an id outside `items`) makes it a root. */
  parentId: string | null;
  data: T;
  /** Draw the edge from this node's parent dashed + animated (marching ants),
   * in the theme's primary color — the "context" ancestor line in a scoped
   * mini chart (who manages the focused person, or a stub root). */
  dashedEdge?: boolean;
  /** `'placeholder'` renders a filled info-blue pill (e.g. an "unassigned"
   * stub) instead of the content card — non-interactive, no selection ring. */
  variant?: "default" | "placeholder";
  /** The node's accessible name, for example the person it shows. */
  label?: string;
}

export type OrgChartProps<T = unknown> = Omittable<{
  items: OrgChartItem<T>[];
  /** Content inside each node card (the card shell, selection ring, press
   * affordance, and collapse chip are owned by the chart). */
  renderNode: (item: OrgChartItem<T>) => React.ReactNode;
  onNodePress?: (id: string) => void;
  /** Highlighted node — themed border ring (e.g. the profile being viewed). */
  selectedId?: string | null;
  /** Center the viewport on this node — on mount (instead of fitting the
   *  whole tree) and again whenever the id changes (jump-to-node search).
   *  Collapsed ancestors of the target auto-expand so it's always visible. */
  focusId?: string | null;
  /** Nodes whose reports start hidden. */
  defaultCollapsedIds?: string[];
  /** Bottom-left view tools (fit view + zoom cluster, with the 1/0/+/− view
   *  hotkeys). Default true. */
  controls?: boolean;
  /** Overview minimap — bottom-left above the view tools, shown only while
   *  the viewport moves and for a few seconds after. Never rendered on a
   *  phone-width screen, where it would cover a real share of the chart.
   *  Default false. */
  minimap?: boolean;
  /** "Expand all (N)" pill (bottom-right) that clears every collapsed node.
   *  Default false. */
  expandable?: boolean;
  /** Host tools floating over the canvas's top-right corner (e.g. a
   *  jump-to-node search) — a canvas panel, so they overlay the chart
   *  instead of costing a chrome band above it. The bottom corners stay the
   *  chart's own (view tools, expand-all, minimap). */
  actions?: React.ReactNode;
  /** The canvas's dot-matrix pattern. Default true; turn off for a scoped
   *  mini chart embedded in a panel, where the dots read as a second surface
   *  behind the host's own. */
  dotGrid?: boolean;
  nodeWidth?: number;
  nodeHeight?: number;
}>;

const DEFAULT_NODE_WIDTH = 224;
const DEFAULT_NODE_HEIGHT = 56;
/** The placeholder stub is a slim pill — shorter than content cards. */
const PLACEHOLDER_HEIGHT = 40;
/** One framing for every fit: opening fit, the Fit view control, and the "1"
 *  hotkey. maxZoom rests a touch under 1:1 so a small chart opens with a bit
 *  of air instead of full-size cards. */
const FIT_VIEW_OPTIONS = { padding: 0.2, maxZoom: 0.9 };
/** The opening frame's floor: a tree too big to fit opens at a zoom whose
 *  cards can still be read, centered on the tree, instead of shrinking every
 *  card to a speck. The Fit view control and the "1" hotkey stay unfloored —
 *  pressing them asks for the whole tree. */
const OPEN_FIT_VIEW_OPTIONS = { ...FIT_VIEW_OPTIONS, minZoom: 0.6 };

// Indented-tree geometry. The trunk (and its collapse chip) hangs below the
// node's left portion at TRUNK_OFFSET; each level of reports shifts right by
// INDENT, so the elbow's horizontal run into a child is INDENT − TRUNK_OFFSET.
const TRUNK_OFFSET = 32;
const INDENT = 72;
/** Tight vertical gap between stacked nodes without a collapse chip —
 *  sibling report cards read as a dense list. */
const V_GAP = 16;
/** Gap below a node that carries a collapse chip (it has reports, shown or
 *  hidden): the chip hangs ~40px into the gap and needs clearance. */
const V_GAP_CHIP = 56;
/** Horizontal gap between sibling root subtrees. */
const ROOT_GAP = 80;

// React Flow paints the minimap and the pane's dot pattern into in-document
// SVG attributes, which resolve var() and re-resolve on a theme flip — so
// these are token references, not computed values.
const DOT_COLOR = "color-mix(in oklab, var(--muted-foreground) 22%, transparent)";
const MINIMAP_MASK = "color-mix(in oklab, var(--foreground) 6%, transparent)";
const MINIMAP_NODE = "color-mix(in oklab, var(--muted-foreground) 40%, transparent)";
const MINIMAP_PLACEHOLDER_NODE = "color-mix(in oklab, var(--info) 55%, transparent)";

type PersonNodeData<T> = {
  item: OrgChartItem<T>;
  isSelected: boolean;
  /** Direct reports (visible or not) — drives the collapse chip. */
  childCount: number;
  collapsed: boolean;
  width: number;
  height: number;
  onToggleCollapse: (id: string) => void;
  onNodeClick: ((id: string) => void) | undefined;
  renderContent: (item: OrgChartItem<T>) => React.ReactNode;
};

/**
 * React Flow types `node.data` as an open record; the chart is the only
 * writer of that record, so this is the one place the shape is reasserted.
 */
function readNodeData<T>(node: { data?: unknown }): PersonNodeData<T> | undefined {
  return node.data as PersonNodeData<T> | undefined;
}
/** The write side of `readNodeData`: the record React Flow stores per node. */
function toNodeData<T>(data: PersonNodeData<T>): Record<string, unknown> {
  return data as unknown as Record<string, unknown>;
}

// ── Tree resolution ──────────────────────────────────────────────────────────

type ResolvedTree<T> = {
  /** Items whose ancestors are all expanded, in BFS order. */
  visible: OrgChartItem<T>[];
  childrenOf: Map<string, OrgChartItem<T>[]>;
};

/**
 * BFS from the roots, stopping at collapsed nodes. Also breaks accidental
 * cycles (bad data should never render as an infinite walk) by refusing to
 * visit a node twice.
 */
function resolveTree<T>(
  items: OrgChartItem<T>[],
  collapsed: ReadonlySet<string>,
): ResolvedTree<T> {
  const ids = new Set(items.map((i) => i.id));
  const childrenOf = new Map<string, OrgChartItem<T>[]>();
  const roots: OrgChartItem<T>[] = [];
  for (const item of items) {
    if (item.parentId !== null && ids.has(item.parentId)) {
      const siblings = childrenOf.get(item.parentId);
      if (siblings) siblings.push(item);
      else childrenOf.set(item.parentId, [item]);
    } else {
      roots.push(item);
    }
  }

  const visible: OrgChartItem<T>[] = [];
  const seen = new Set<string>();
  const queue = [...roots];
  while (queue.length > 0) {
    const item = queue.shift()!;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    visible.push(item);
    if (!collapsed.has(item.id)) queue.push(...(childrenOf.get(item.id) ?? []));
  }
  return { visible, childrenOf };
}

// ── Layout ───────────────────────────────────────────────────────────────────

/**
 * Indented-tree layout: DFS each root, placing every visible report one
 * INDENT right of its parent and stacking the whole subtree vertically.
 * Roots sit side by side, each given its subtree's full horizontal extent.
 */
function layoutPositions<T>(
  visible: OrgChartItem<T>[],
  childrenOf: Map<string, OrgChartItem<T>[]>,
  width: number,
  height: number,
): Map<string, { x: number; y: number }> {
  const visibleIds = new Set(visible.map((i) => i.id));
  const roots = visible.filter(
    (i) => i.parentId === null || !visibleIds.has(i.parentId),
  );
  const positions = new Map<string, { x: number; y: number }>();

  const visibleChildren = (id: string) =>
    (childrenOf.get(id) ?? []).filter((child) => visibleIds.has(child.id));

  // Place a subtree; returns the next free y below it. The gap below a node
  // depends on whether it carries a collapse chip (any reports, visible or
  // collapsed away) — chip-less siblings pack tightly.
  const place = (item: OrgChartItem<T>, x: number, y: number): number => {
    if (positions.has(item.id)) return y; // cycle/dup guard
    positions.set(item.id, { x, y });
    const ownHeight =
      item.variant === "placeholder" ? PLACEHOLDER_HEIGHT : height;
    const hasChip = (childrenOf.get(item.id)?.length ?? 0) > 0;
    let nextY = y + ownHeight + (hasChip ? V_GAP_CHIP : V_GAP);
    for (const child of visibleChildren(item.id)) {
      nextY = place(child, x + INDENT, nextY);
    }
    return nextY;
  };

  // Widest reach of a subtree, in absolute x from its root's origin.
  const extent = (item: OrgChartItem<T>, depth: number): number => {
    let max = depth * INDENT + width;
    for (const child of visibleChildren(item.id)) {
      max = Math.max(max, extent(child, depth + 1));
    }
    return max;
  };

  let rootX = 0;
  for (const root of roots) {
    place(root, rootX, 0);
    rootX += extent(root, 0) + ROOT_GAP;
  }
  return positions;
}

// ── Node ─────────────────────────────────────────────────────────────────────

/**
 * Collapse toggle sitting ON the trunk, just below the node's left portion
 * (at TRUNK_OFFSET — where the reporting line drops from). Collapsed → a
 * "+N" pill (N hidden reports); expanded → a "−" square the trunk passes
 * through. A tooltip names the action. Nothing renders without reports.
 */
function CollapseChip({
  childCount,
  collapsed,
  onToggle,
}: {
  childCount: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  if (childCount <= 0) return null;
  const reports = `${childCount} direct report${childCount === 1 ? "" : "s"}`;
  const label = collapsed ? `Expand ${reports}` : `Collapse ${reports}`;
  return (
    <Box
      // `nopan` keeps a press on the chip from being eaten by pane panning.
      // pt-3 floats the chip clear of the card so it reads as a control on
      // the trunk, not a barnacle on the border.
      className="nopan top-full -translate-x-1/2 pt-3"
      left={TRUNK_OFFSET}
      position="absolute"
      zIndex={10}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <Box
              align="center"
              aria-label={label}
              as="button"
              className={cn(
                "size-7 rounded-md border border-border bg-card shadow-sm",
                // Neutral hover, matching the node cards — no themed ring.
                "transition-colors hover:border-muted-foreground/40 hover:bg-muted",
              )}
              cursor="pointer"
              direction="row"
              justify="center"
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                // Stop before the React Flow node wrapper, whose onNodeClick
                // would otherwise ALSO fire (pressing the node itself).
                event.stopPropagation();
                onToggle();
              }}
            >
              {collapsed ? (
                // Visual cap: past nine the chip reads "9+" (the avatar
                // stacks' overflow grammar), never a count that looks exact;
                // the tooltip carries the real number.
                <Text
                  className="tabular-nums"
                  color="muted"
                  size="sm"
                  weight="medium"
                >
                  {childCount > 9 ? "9+" : `+${childCount}`}
                </Text>
              ) : (
                <Icon color="muted" name="minus" />
              )}
            </Box>
          }
        />
        <TooltipPopup side="top">{label}</TooltipPopup>
      </Tooltip>
    </Box>
  );
}

function OrgChartFlowNode(props: NodeProps) {
  const data = readNodeData<unknown>(props);
  if (!data) return null;
  const {
    item,
    isSelected,
    childCount,
    collapsed,
    width,
    height,
    onToggleCollapse,
    onNodeClick,
    renderContent,
  } = data;

  // A placeholder stub: filled info pill, non-interactive, only a source
  // anchor so the dashed context edge can drop to the node below. Fixed blue
  // (`info`), never the workspace's brand/primary color — the stub reads as a
  // neutral system marker, not a themed entity. Mixed toward black because
  // raw `info` is loud as a large fill (its dark value sits at 71% lightness
  // — glaring on a dark canvas); the deeper dark-mode mix lands both modes at
  // a similar calm depth. The pill is a solid tile, so it carries the solid-
  // tile ink: white in both modes, inherited by whatever `renderContent`
  // paints inside it (an unstyled `Text` label reads white here).
  if (item.variant === "placeholder") {
    return (
      <Box
        align="center"
        className={cn(
          "shadow-md text-white",
          "[background-color:color-mix(in_oklab,var(--info)_82%,black)]",
          "dark:[background-color:color-mix(in_oklab,var(--info)_60%,black)]",
        )}
        direction="row"
        height={PLACEHOLDER_HEIGHT}
        justify="center"
        paddingX="md"
        position="relative"
        rounded="lg"
        width={width}
      >
        <Handle
          className="!pointer-events-none !opacity-0"
          isConnectable={false}
          position={Position.Bottom}
          style={{ left: TRUNK_OFFSET }}
          type="source"
        />
        {renderContent(item)}
        <CollapseChip
          childCount={childCount}
          collapsed={collapsed}
          onToggle={() => onToggleCollapse(item.id)}
        />
      </Box>
    );
  }

  return (
    <Box
      // Press handling lives on the flow's onNodeClick (the node wrapper) —
      // an onClick here would be dead anyway: React Flow disables pointer
      // events on nodes that are neither draggable, selectable, nor clicked
      // via a flow-level handler. The Box only carries the affordance.
      className={cn(
        "border bg-card shadow-md transition-colors",
        // Hover strengthens the border NEUTRALLY (muted-foreground blend) —
        // the themed ring is reserved for the selected state.
        isSelected
          ? "border-primary ring-1 ring-primary"
          : "border-border hover:border-muted-foreground/40",
      )}
      cursor={onNodeClick ? "pointer" : undefined}
      direction="column"
      height={height}
      justify="center"
      paddingX="sm"
      position="relative"
      rounded="lg"
      width={width}
    >
      {/* Invisible anchors — edges need them, the design doesn't. The trunk
       * drops from below-left (TRUNK_OFFSET); incoming elbows land mid-left. */}
      <Handle
        className="!pointer-events-none !opacity-0"
        isConnectable={false}
        position={Position.Left}
        type="target"
      />
      <Handle
        className="!pointer-events-none !opacity-0"
        isConnectable={false}
        position={Position.Bottom}
        style={{ left: TRUNK_OFFSET }}
        type="source"
      />
      {renderContent(item)}
      <CollapseChip
        childCount={childCount}
        collapsed={collapsed}
        onToggle={() => onToggleCollapse(item.id)}
      />
    </Box>
  );
}

const NODE_TYPES = { person: OrgChartFlowNode };

// ── Controls ─────────────────────────────────────────────────────────────────
// The automation canvas's view-tools language, so every React Flow surface
// shares one control grammar: bottom-left cluster of ControlBar cards — Fit
// view standalone, then zoom out/in with a Reset-to-100% control that only
// appears once you've zoomed off default. Same view hotkeys too.

// The chart's resting scale: the opening fit caps here, so "default zoom" —
// what Reset returns to and what hides the reset affordance — is this value,
// not 1:1.
const DEFAULT_ZOOM = FIT_VIEW_OPTIONS.maxZoom;

function OrgChartControls() {
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  // Live zoom, so the reset affordance only appears once you've zoomed off
  // the resting scale.
  const { zoom } = useViewport();
  const isDefaultZoom = Math.round(zoom * 100) === Math.round(DEFAULT_ZOOM * 100);

  // View hotkeys (no modifier): 1 = fit, 0 = reset zoom, +/= zoom in, -/_
  // zoom out. Ignored while a text field is focused and when a modifier is
  // held, so browser/OS shortcuts pass through untouched.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTextEditingTarget(document.activeElement)) return;
      switch (e.key) {
        case "1":
          e.preventDefault();
          void fitView({ duration: 300, ...FIT_VIEW_OPTIONS });
          break;
        case "0":
          e.preventDefault();
          void zoomTo(DEFAULT_ZOOM, { duration: 300 });
          break;
        case "+":
        case "=":
          e.preventDefault();
          void zoomIn({ duration: 200 });
          break;
        case "-":
        case "_":
          e.preventDefault();
          void zoomOut({ duration: 200 });
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fitView, zoomTo, zoomIn, zoomOut]);

  return (
    <Panel position="bottom-left">
      <Box align="center" direction="row" gap="sm">
        {/* Fit view — standalone (leftmost): frames the whole chart, a
            different intent from stepping zoom in/out. */}
        <ControlBar>
          <ControlBarButton
            label="Fit view"
            onClick={() => void fitView({ duration: 300, ...FIT_VIEW_OPTIONS })}
            shortcut={["1"]}
          >
            <Icon name="maximize" />
          </ControlBarButton>
        </ControlBar>

        {/* Zoom cluster — out / in, plus Reset-to-100% once off default. */}
        <ControlBar>
          <Group orientation="horizontal">
            <ControlBarButton
              label="Zoom out"
              onClick={() => void zoomOut()}
              shortcut={["-"]}
            >
              <Icon name="minus" />
            </ControlBarButton>
            <Separator orientation="vertical" />
            <ControlBarButton
              label="Zoom in"
              onClick={() => void zoomIn()}
              shortcut={["+"]}
            >
              <Icon name="plus" />
            </ControlBarButton>
            {!isDefaultZoom ? (
              <>
                <Separator orientation="vertical" />
                <ControlBarButton
                  label="Reset zoom"
                  onClick={() => void zoomTo(DEFAULT_ZOOM, { duration: 300 })}
                  shortcut={["0"]}
                >
                  <Icon name="undo" />
                </ControlBarButton>
              </>
            ) : null}
          </Group>
        </ControlBar>
      </Box>
    </Panel>
  );
}

// ── Chart ────────────────────────────────────────────────────────────────────

function OrgChartInner<T>({
  items,
  renderNode,
  onNodePress,
  selectedId = null,
  focusId = null,
  defaultCollapsedIds,
  controls = true,
  minimap: minimapProp = false,
  expandable = false,
  actions,
  dotGrid = true,
  nodeWidth = DEFAULT_NODE_WIDTH,
  nodeHeight = DEFAULT_NODE_HEIGHT,
}: OrgChartProps<T>) {
  // A phone-width screen has no room to spare for a navigation aid: the
  // minimap would sit over a real share of the chart, and the finger already
  // pans it.
  const isMobile = useIsMobile();
  const minimap = minimapProp && !isMobile;
  const flow = useReactFlow();

  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(
    () => new Set(defaultCollapsedIds ?? []),
  );

  const toggleCollapse = React.useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const expandAll = React.useCallback(() => setCollapsed(new Set()), []);

  // Headcount for the "Expand all" pill — real nodes only, never the
  // placeholder stub.
  const memberCount = React.useMemo(
    () => items.filter((i) => i.variant !== "placeholder").length,
    [items],
  );

  // Every node with reports — the collapse-all target, and the set that
  // decides whether anything is currently collapsed (which flips the
  // bottom-right action between Expand all and Collapse all).
  const parentIds = React.useMemo(() => {
    const ids = new Set(items.map((i) => i.id));
    const parents = new Set<string>();
    for (const item of items) {
      if (item.parentId !== null && ids.has(item.parentId)) {
        parents.add(item.parentId);
      }
    }
    return parents;
  }, [items]);
  const anyCollapsed = React.useMemo(
    () => [...parentIds].some((id) => collapsed.has(id)),
    [parentIds, collapsed],
  );
  const collapseAll = React.useCallback(
    () => setCollapsed(new Set(parentIds)),
    [parentIds],
  );

  // Node presses route through the flow-level handler — React Flow disables
  // pointer events on nodes that are neither draggable, selectable, nor
  // observed by a flow click handler, which would also kill the collapse
  // chip. The chip stops propagation before this fires.
  const handleNodeClick = React.useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const nodeData = readNodeData<T>(node);
      if (nodeData?.item?.variant === "placeholder") return;
      onNodePress?.(node.id);
    },
    [onNodePress],
  );

  // Minimap visibility: it's a navigation aid, so it only appears while the
  // viewport is actually moving (pan/zoom/fit — user or programmatic) and
  // lingers a few seconds after, then fades out (styled via index.css
  // targeting React Flow's own `.react-flow__minimap` class; the fade flag
  // lives on the wrapper Box — no consumer-side className).
  const MINIMAP_LINGER_MS = 3000;
  const [minimapVisible, setMinimapVisible] = React.useState(false);
  const minimapTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onViewportMove = React.useCallback(() => {
    setMinimapVisible(true);
    if (minimapTimer.current) clearTimeout(minimapTimer.current);
    minimapTimer.current = setTimeout(
      () => setMinimapVisible(false),
      MINIMAP_LINGER_MS,
    );
  }, []);
  React.useEffect(
    () => () => {
      if (minimapTimer.current) clearTimeout(minimapTimer.current);
    },
    [],
  );

  // Minimap nodes: quiet pills, not bars. Each card reads at 40% of the
  // secondary ink so the map is a silhouette of the chart, and the
  // placeholder stub keeps its info-blue. In-document SVG resolves var() and
  // color-mix() directly.
  const miniMapNodeColor = React.useCallback((node: Node) => {
    const nodeData = readNodeData<T>(node);
    return nodeData?.item?.variant === "placeholder"
      ? MINIMAP_PLACEHOLDER_NODE
      : MINIMAP_NODE;
  }, []);

  const { nodes, edges } = React.useMemo(() => {
    const { visible, childrenOf } = resolveTree(items, collapsed);
    const positions = layoutPositions(visible, childrenOf, nodeWidth, nodeHeight);
    const visibleIds = new Set(visible.map((i) => i.id));

    const flowNodes: Node[] = visible.map((item) => {
      const data: PersonNodeData<T> = {
        item,
        isSelected: item.id === selectedId,
        childCount: childrenOf.get(item.id)?.length ?? 0,
        collapsed: collapsed.has(item.id),
        width: nodeWidth,
        height: nodeHeight,
        onToggleCollapse: toggleCollapse,
        onNodeClick: onNodePress,
        renderContent: renderNode,
      };
      return {
        id: item.id,
        type: "person",
        position: positions.get(item.id) ?? { x: 0, y: 0 },
        // Explicit dimensions (we own the card sizes): the minimap and
        // fitView read these immediately — without them they'd wait on DOM
        // measurement, which resets every time this memo rebuilds the node
        // objects (collapse/selection changes), leaving the minimap with
        // nothing to draw.
        width: nodeWidth,
        height: item.variant === "placeholder" ? PLACEHOLDER_HEIGHT : nodeHeight,
        data: toNodeData(data),
        ...(item.label ? { ariaLabel: item.label } : {}),
        draggable: false,
        connectable: false,
        selectable: false,
      };
    });

    const flowEdges: Edge[] = visible
      .filter((item) => item.parentId !== null && visibleIds.has(item.parentId))
      .map((item) => {
        // Context edges (dashed) trace up to the ancestor / placeholder stub:
        // primary-colored, marching-ants animation. Report edges (solid) are
        // the quiet grey reporting lines.
        const dashed = item.dashedEdge ?? false;
        const stroke = dashed ? "var(--primary)" : "var(--border)";
        return {
          id: `e-${item.parentId}-${item.id}`,
          source: item.parentId!,
          target: item.id,
          // Trunk-and-elbow: down from the parent's TRUNK_OFFSET anchor, then
          // right into the child's left edge. Sibling edges overlap on the
          // shared vertical run, reading as one trunk with elbows peeling off.
          type: "smoothstep",
          pathOptions: { borderRadius: 8 },
          animated: dashed,
          style: {
            stroke,
            strokeWidth: 1.5,
            strokeDasharray: dashed ? "6 5" : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: stroke,
            width: 16,
            height: 16,
          },
        };
      });

    return { nodes: flowNodes, edges: flowEdges };
  }, [
    items,
    collapsed,
    selectedId,
    nodeWidth,
    nodeHeight,
    toggleCollapse,
    onNodePress,
    renderNode,
  ]);

  // A focus target may sit under collapsed ancestors (e.g. inside a collapsed
  // stub bucket) — expand the chain above it so the node exists for the
  // centering effect below.
  React.useEffect(() => {
    if (!focusId) return;
    setCollapsed((prev) => {
      if (prev.size === 0) return prev;
      const parentOf = new Map(items.map((i) => [i.id, i.parentId]));
      const next = new Set(prev);
      let changed = false;
      // Hop cap mirrors resolveTree's cycle defensiveness.
      let cursor = parentOf.get(focusId) ?? null;
      for (let hops = 0; cursor && hops < 1000; hops++) {
        if (next.delete(cursor)) changed = true;
        cursor = parentOf.get(cursor) ?? null;
      }
      return changed ? next : prev;
    });
  }, [focusId, items]);

  // Center the viewport on the focused node — on mount (deep link) and again
  // on every focus change (a jump-to-node search re-targets the same chart).
  const lastFocusRef = React.useRef<string | null>(null);
  // The opening frame, captured once for the fitView PROP: a deep link opens
  // centered on its target, anything else fits the whole tree.
  const initialFitOptions = React.useRef(
    focusId
      ? { nodes: [{ id: focusId }], maxZoom: DEFAULT_ZOOM, padding: 0.5 }
      : OPEN_FIT_VIEW_OPTIONS,
  );
  // Whether the viewport has been framed at least once (the prop's initial
  // fit counts once nodes initialize). The first fit the focus effect itself
  // performs lands instantly when the prop fit couldn't see its target —
  // animating would glide in from wherever init left the viewport.
  const didFitRef = React.useRef(!focusId);
  React.useEffect(() => {
    if (!focusId) {
      // Focus CLEARED (not merely absent): glide back out to the whole-tree
      // fit — the symmetric exit from the zoomed single-node framing. A
      // focus-less mount takes the other branch of the fitView prop and must
      // not double-fit here.
      if (lastFocusRef.current !== null) {
        lastFocusRef.current = null;
        void flow.fitView({ ...FIT_VIEW_OPTIONS, duration: 300 });
      }
      return;
    }
    // Before the viewport initializes an imperative fitView is a silent no-op
    // (React Flow stamps its initial viewport after it) — `flow` re-creates
    // when the flag flips, so this effect re-fires and catches up.
    if (!flow.viewportInitialized) return;
    if (focusId === lastFocusRef.current) return;
    if (!nodes.some((n) => n.id === focusId)) return;
    lastFocusRef.current = focusId;
    void flow.fitView({
      nodes: [{ id: focusId }],
      maxZoom: FIT_VIEW_OPTIONS.maxZoom,
      padding: 0.5,
      duration: didFitRef.current ? 300 : 0,
    });
    didFitRef.current = true;
  }, [focusId, nodes, flow]);

  return (
    <Box
      data-minimaphidden={minimapVisible ? "false" : "true"}
      data-slot="org-chart"
      fill
      position="relative"
    >
      <ReactFlow
        edges={edges}
        elementsSelectable={false}
        // React Flow owns the OPENING frame — an imperative fitView issued
        // around init loses to the default viewport it stamps. A deep link
        // targets the focused node; the focus effect above handles later
        // jumps (and re-frames if the target starts hidden under a collapsed
        // ancestor).
        fitView
        fitViewOptions={initialFitOptions.current}
        maxZoom={1.5}
        minZoom={0.2}
        nodeTypes={NODE_TYPES}
        nodes={nodes}
        nodesConnectable={false}
        nodesDraggable={false}
        {...(minimap ? { onMove: onViewportMove } : null)}
        onNodeClick={handleNodeClick}
        proOptions={{ hideAttribution: true }}
        zoomOnDoubleClick={false}
      >
        {dotGrid && (
          <Background
            color={DOT_COLOR}
            gap={24}
            size={1.5}
            variant={BackgroundVariant.Dots}
          />
        )}
        {controls && <OrgChartControls />}
        {actions != null && <Panel position="top-right">{actions}</Panel>}
        {expandable && (
          <Panel position="bottom-right">
            {/* Same chrome + control height as the view-tools cards, so the
             * bottom rail reads as one family of canvas actions. Toggles to
             * Collapse all once the whole tree is expanded. The label is ONE
             * string — separate JSX text nodes become gap-separated flex
             * children inside Button and "(2)" renders as "( 2 )". */}
            <ControlBar>
              {anyCollapsed ? (
                <Button
                  aria-label={`Expand all (${memberCount})`}
                  className="rounded-none before:rounded-none"
                  onClick={expandAll}
                  variant="ghost"
                >
                  <Icon name="chevrons-up-down" />
                  {`Expand all (${memberCount})`}
                </Button>
              ) : (
                <Button
                  aria-label={`Collapse all (${memberCount})`}
                  className="rounded-none before:rounded-none"
                  onClick={collapseAll}
                  variant="ghost"
                >
                  <Icon name="chevrons-down-up" />
                  {`Collapse all (${memberCount})`}
                </Button>
              )}
            </ControlBar>
          </Panel>
        )}
        {minimap && (
          <MiniMap
            ariaLabel="Org chart minimap"
            bgColor="var(--card)"
            maskColor={MINIMAP_MASK}
            // Half the card height: every node is a full pill, whatever the
            // chart's zoom.
            nodeBorderRadius={nodeHeight / 2}
            nodeColor={miniMapNodeColor}
            nodeStrokeWidth={0}
            pannable
            position="bottom-left"
            zoomable
          />
        )}
      </ReactFlow>
    </Box>
  );
}

export function OrgChart<T = unknown>(props: OrgChartProps<T>) {
  return (
    <ReactFlowProvider>
      <OrgChartInner {...props} />
    </ReactFlowProvider>
  );
}
